import { formatDue, isOverdue } from '@/lib/dates.ts';
import { useUpdateTask } from '@/hooks/useTasks.ts';
import { Checkbox, Tag } from './ui.tsx';
import { PRIORITY_LABELS } from '@shared/types';
import type { TaskWithRelations } from '@shared/types';

/**
 * Priority as rail height, not colour.
 *
 * The app used to encode priority twice and differently — a coloured dot in
 * the lists, a text label in the table. Here one system covers both: the bar's
 * HEIGHT carries the level, so it survives greyscale and colour blindness, and
 * the label is still available to assistive tech and in the table column.
 */
const RAIL_HEIGHT: Record<number, string> = { 0: '0px', 1: '8px', 2: '14px', 3: '20px' };
const RAIL_TONE: Record<number, string> = {
  0: 'transparent',
  1: 'var(--color-brass-dim)',
  2: 'color-mix(in oklab, var(--color-brass) 70%, var(--color-brass-dim))',
  3: 'var(--color-brass)',
};

interface Props {
  task: TaskWithRelations;
  onOpen: (id: string) => void;
  showProject?: boolean;
  projectName?: string | null;
}

export function TaskRow({ task, onOpen, showProject = true, projectName }: Props) {
  const updateTask = useUpdateTask();
  const isDone = task.status === 'done' || task.status === 'cancelled';
  const overdue = !isDone && isOverdue(task.due_at);

  return (
    <li className="rule-inset group relative">
      {/* The ruled margin. Brick when late, brass otherwise — height and colour
          are separate channels, so neither carries the meaning alone. */}
      <span
        aria-hidden="true"
        className="rail"
        style={{
          height: isDone ? '0px' : RAIL_HEIGHT[task.priority],
          background: overdue ? 'var(--color-brick)' : RAIL_TONE[task.priority],
        }}
      />

      <div className="flex items-start gap-3 rounded-xs py-2 pr-2 pl-3 transition-colors duration-[120ms] ease-out group-hover:bg-hover/50">
        <span className="pt-0.5">
          <Checkbox
            checked={isDone}
            onChange={() => updateTask.mutate({ id: task.id, status: isDone ? 'todo' : 'done' })}
            label={isDone ? `Reopen ${task.title}` : `Complete ${task.title}`}
          />
        </span>

        <button
          type="button"
          onClick={() => onOpen(task.id)}
          className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left sm:flex-row sm:items-baseline sm:gap-3"
        >
          <span
            className={`min-w-0 flex-1 truncate transition-colors duration-[120ms] ease-out ${
              isDone ? 'text-ink-3 line-through' : 'text-ink'
            } ${task.priority === 3 && !isDone ? 'font-medium' : ''}`}
          >
            {task.title}
            {task.priority > 0 && !isDone && (
              <span className="sr-only"> — {PRIORITY_LABELS[task.priority]} priority</span>
            )}
          </span>

          {/* Metadata right-aligns into columns at width, so the list reads as
              a ledger rather than a ragged sentence. */}
          <span className="flex shrink-0 items-baseline gap-3">
            {task.subtask_count > 0 && (
              <Tag>
                <span data-numeric>
                  {task.subtask_done_count}/{task.subtask_count}
                </span>
              </Tag>
            )}
            {task.tags.slice(0, 2).map((tag) => (
              <Tag key={tag.id}>#{tag.name}</Tag>
            ))}
            {showProject && projectName && (
              <span className="t-meta hidden w-28 truncate text-right text-ink-3 sm:inline-block">
                {projectName}
              </span>
            )}
            {task.due_at ? (
              <time
                dateTime={task.due_at}
                className={`t-meta w-24 shrink-0 text-right ${overdue ? 'text-brick' : 'text-ink-2'}`}
              >
                {overdue && <span aria-hidden="true">· </span>}
                {formatDue(task.due_at)}
              </time>
            ) : (
              <span aria-hidden="true" className="hidden w-24 shrink-0 sm:block" />
            )}
          </span>
        </button>
      </div>
    </li>
  );
}
